import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import PropTypes from 'prop-types'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import LinkedInvoice from './LinkedInvoice'
import { formatDate, formatNumber } from '@atheneum/api/dist/lib'

class InvoiceDetails extends Component {
  static propTypes = {
    invoiceStore: PropTypes.object,
    staticData: PropTypes.object,
    sidebarStore: PropTypes.object,
    project: PropTypes.object,
    contract: PropTypes.object
  }

  renderDateInfo = (staticData, invoiceStore) => {
    const { INVOICE_STATUSES } = staticData.CONST
    const { invoiceData } = invoiceStore
    if (invoiceData.invoiceStatusId < INVOICE_STATUSES.CREATED) return null

    return (
      <div className='m-b-xl'>
        <div className='invoice-details__item'>
          <span>Sent date</span>
          <span>{invoiceData.sentDate}</span>
        </div>
        <div className='invoice-details__item'>
          <span>Due date</span>
          <span>{invoiceData.dueDate}</span>
        </div>
        <div className='invoice-details__item'>
          <span>Document date</span>
          <span>{invoiceData.documentDate}</span>
        </div>
      </div>
    )
  }

  renderApprovedInfo = (staticData, invoiceStore) => {
    const { INVOICE_STATUSES } = staticData.CONST
    const { invoiceData } = invoiceStore

    if (invoiceData.invoiceStatusId < INVOICE_STATUSES.APPROVED) return null
    if (!invoiceData.approvedBy) return null

    return (
      <div className='m-b-xl'>
        {invoiceData.approvedBy && (
          <div className='invoice-details__item'>
            <span>Approved by</span>
            <span>{invoiceData.approvedBy}</span>
          </div>
        )}

        {invoiceData.approvedDate && (
          <div className='invoice-details__item'>
            <span>Approved on</span>
            <span>{invoiceData.approvedDate}</span>
          </div>
        )}
      </div>
    )
  }

  renderDisapprovedInfo = (staticData, invoiceStore) => {
    const { INVOICE_STATUSES } = staticData.CONST
    const { invoiceData } = invoiceStore

    if (invoiceData.invoiceStatusId !== INVOICE_STATUSES.DRAFT) return null
    if (!invoiceData.disapprovedBy) return null

    return (
      <div className='m-b-xl'>
        {invoiceData.disapprovedBy && (
          <div className='invoice-details__item'>
            <span>Disapproved by</span>
            <span>{invoiceData.disapprovedBy}</span>
          </div>
        )}

        {invoiceData.disapprovedDate && (
          <div className='invoice-details__item'>
            <span>Disapproved on</span>
            <span>{invoiceData.disapprovedDate}</span>
          </div>
        )}

        {invoiceData.disapprovedReason && (
          <div className='invoice-details__item'>
            <span>Reason for disapproval</span>
            <span className='text'>{invoiceData.disapprovedReason}</span>
          </div>
        )}
      </div>
    )
  }

  renderAmountAndConversionInfo = (staticData, invoiceStore) => {
    const { currencies } = staticData.names
    const { INVOICE_STATUSES } = staticData.CONST
    const { invoice } = invoiceStore

    if (invoice.invoiceStatusId < INVOICE_STATUSES.CREATED) return null

    const conversionRate = invoice.invoiceItems.length
      ? formatNumber(invoice.invoiceItems[0].conversionRate, 6)
      : 'N/A'

    return (
      <div className='m-b-xl'>
        <div className='invoice-details__item'>
          <span>Invoice amount in EUR</span>
          <span className='text'>{formatNumber(invoice.netAmountEur)}</span>
        </div>
        <div className='invoice-details__item'>
          <span>
            {currencies[invoice.currencyId]}
            /EUR conversion rate
          </span>
          <span>{conversionRate}</span>
        </div>
      </div>
    )
  }

  renderInvoiceSentToClientInfo = invoiceStore => {
    const { invoice } = invoiceStore

    if (!invoice.invoiceSentToClientDate) return null

    return (
      <div className='m-b-xl'>
        <div className='invoice-details__item'>
          <span>Last sent to client</span>
          <span className='text'>
            {formatDate(invoice.invoiceSentToClientDate)}
          </span>
        </div>
      </div>
    )
  }

  renderInvoicingInstructions = ({ invoiceStore, project, contract }) => {
    const invoicingInstructions = invoiceStore.isPayg
      ? project.invoicingInstructions
      : contract.invoicingInstructions

    if (!invoicingInstructions) return null

    return (
      <InfoBox className='m-b-xxl' theme='yellow' icon={<WarningIcon />}>
        <div dangerouslySetInnerHTML={{ __html: invoicingInstructions }} />
      </InfoBox>
    )
  }

  render() {
    const {
      invoiceStore,
      staticData,
      sidebarStore,
      project,
      contract
    } = this.props
    const { invoiceData, invoice } = invoiceStore
    const { INVOICE_STATUSES } = staticData.CONST

    if (!invoiceData) return null

    return (
      <div>
        {this.renderInvoicingInstructions({
          invoiceStore,
          contract,
          project
        })}

        <div className='invoice-details__group'>
          <LinkedInvoice invoice={invoice} />

          <div className='invoice-details__item'>
            <span>Invoice type</span>
            <span>{invoiceData.invoiceType}</span>
          </div>

          <div className='invoice-details__item link'>
            <span>Invoicing entity</span>
            <span
              onClick={() =>
                sidebarStore.openSidebar({
                  id: invoice.invoicingEntity.id,
                  template: 'invoicing-entity'
                })
              }
            >
              {invoiceData.invoicingEntity.legalName}
            </span>
          </div>

          <div className='invoice-details__item'>
            <span>Atheneum company</span>
            <span>{invoiceData.atheneumOffice.name}</span>
          </div>

          <div className='invoice-details__item'>
            <span>VAT (%)</span>
            <span>{formatNumber(invoiceData.vat)}</span>
          </div>

          <div className='invoice-details__item'>
            <span>Case code</span>
            <span>{invoiceData.caseCode || 'N/A'}</span>
          </div>

          <div className='invoice-details__item'>
            <span>Currency</span>
            <span>{invoiceData.currency}</span>
          </div>

          {invoice.engagementManagerList && (
            <div className='invoice-details__item'>
              <span>Engagement {invoice.iskpmgAccount ? 'Partner' : 'Manager'}</span>
              <span>{invoice.engagementManagerList}</span>
            </div>
          )}

          {invoice.engagementCodeList && (
            <div className='invoice-details__item'>
              <span>Engagement Code</span>
              <span>{invoice.engagementCodeList}</span>
            </div>
          )}

          {invoiceStore.isNonPayg && (
            <div className='invoice-details__item'>
              <span>Period of service</span>
              <span>{invoiceData.periodOfService}</span>
            </div>
          )}

          {invoiceData.invoiceStatusId >= INVOICE_STATUSES.CREATED && (
            <div className='invoice-details__item'>
              <span>Draft name</span>
              <span>{invoiceData.draftName}</span>
            </div>
          )}

          {invoice.isCreditNote && (
            <div className='invoice-details__item'>
              <span>Write-off</span>
              <span>{invoiceData.isWriteOff ? 'Yes' : 'No'}</span>
            </div>
          )}
        </div>

        <div className='invoice-details__group'>
          {this.renderDateInfo(staticData, invoiceStore)}
          {this.renderApprovedInfo(staticData, invoiceStore)}
          {this.renderDisapprovedInfo(staticData, invoiceStore)}
          {this.renderAmountAndConversionInfo(staticData, invoiceStore)}
          {this.renderInvoiceSentToClientInfo(invoiceStore)}
        </div>
      </div>
    )
  }
}

const ObservableDetails = inject(
  'invoiceStore',
  'staticData',
  'contractStore',
  'sidebarStore'
)(observer(InvoiceDetails))

export default ObservableDetails
